import React from 'react';

import {
  Checkbox,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Typography,
} from '@material-ui/core';
import clsx from 'clsx';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import ColoredButton from 'components/ColoredButton';
import CustomAccordion from 'components/CustomAccordion';
import ErrorBoundary from 'components/ErrorBoundary';
import FormSelect from 'components/FormSelect';
import FormTextInput from 'components/FormTextInput';
import Loader from 'components/Loader';
import ManagePageHeader from 'components/ManagePageHeader/ManagePageHeader';
import dsarApi from 'config/api/dsar/dsar';
import type {
  DsarCaseAsset,
  DsarCaseAssetRequestTypeResult,
  DsarCaseAssetStatusLogCreatePayload,
  DsarCaseAssetRequestTypeUpdatePayload,
  DsarCaseAssetUpdatePayload,
  DsarDomainValueSummary,
} from 'config/api/dsar/dsar.types';
import dsar_messages from 'messages/dsar_messages';
import general_messages from 'messages/general_messages';
import PATHS from 'router/PATHS';

import useStyles from './DsarCaseAssetDetailPage.styles';

const CASE_ASSET_STATUS_GROUP = 'case_asset_status';
const CASE_ASSET_DATA_FOUND_GROUP = 'case_asset_data_found';
const CASE_ASSET_REQUEST_TYPE_STATUS_GROUP = 'case_asset_request_type_status';
const CASE_ASSET_COMPLETED_STATUS_CODE = 'completed';
const REQUEST_TYPE_BLOCKING_STATUS_CODES = ['deferred', 'not_possible'];
const REQUEST_TYPE_FINAL_STATUS_CODES = ['completed', 'partially_completed', 'not_applicable', 'deferred', 'not_possible'];

type LocationState = {
  openEdit?: boolean;
};

type FieldProps = {
  label: string;
  value?: React.ReactNode;
};

type AssetSummarySectionProps = {
  caseAsset: DsarCaseAsset;
  dataFoundValues: DsarDomainValueSummary[];
  editMode: boolean;
  onCompleted: () => void;
  onSaved: (updatedCaseAsset: DsarCaseAsset) => void;
  statusValues: DsarDomainValueSummary[];
};

type RequestTypeSectionProps = {
  editMode: boolean;
  handlingStatusValues: DsarDomainValueSummary[];
  onSaved: (updatedResult: DsarCaseAssetRequestTypeResult) => void;
  result: DsarCaseAssetRequestTypeResult;
};

type AssetSummaryValues = {
  dataFoundId: number | '';
  resultSummary: string;
  assetOwnerComment: string;
  thirdPartyDataRisk: boolean;
  manualReviewRequired: boolean;
};

type AssetStatusLogValues = {
  statusId: number | '';
  comment: string;
};

type CompletionValues = AssetSummaryValues;

type RequestTypeValues = {
  handlingStatusId: number | '';
  resultSummary: string;
  blockingReason: string;
  earliestPossibleActionDate: string;
};

const toOptions = (values?: DsarDomainValueSummary[]) => (values || []).map(value => ({ key: value.id, label: value.name }));

const cleanOptional = (value: string) => value.trim() || undefined;

const toDateInputValue = (value?: string | null) => {
  if (!value) return '';
  return value.split('T')[0];
};

const getRequestTypeStatusGuidanceMessage = (statusCode: string, t: (message: string | string[]) => string) => {
  if (statusCode === 'deferred') return t(dsar_messages.case_assets.guidance.deferred);
  if (statusCode === 'not_possible') return t(dsar_messages.case_assets.guidance.not_possible);
  if (statusCode && statusCode !== 'not_started') return t(dsar_messages.case_assets.guidance.started);
  return t(dsar_messages.case_assets.guidance.not_started);
};

const formatDate = (value?: string | null) => (value ? value.split('T')[0] : '-');
const formatDateTime = (value?: string | null) => (value ? value.replace('T', ' ').slice(0, 16) : '-');
const isRequestTypeFinal = (result: DsarCaseAssetRequestTypeResult) => REQUEST_TYPE_FINAL_STATUS_CODES.includes(result.handlingStatus.code);
const getApiErrorMessage = (error: any) => {
  const detail = error?.response?.data;
  if (!detail || typeof detail !== 'object') return null;

  const [firstValue] = Object.values(detail);
  if (Array.isArray(firstValue)) return firstValue[0];
  if (typeof firstValue === 'string') return firstValue;
  return null;
};

const formatAddress = (caseSummary: DsarCaseAsset['dsarCase']) =>
  [
    caseSummary.requesterAddressLine1,
    caseSummary.requesterAddressLine2,
    [caseSummary.requesterPostalCode, caseSummary.requesterCity].filter(Boolean).join(' '),
    caseSummary.requesterCountry,
  ]
    .filter(Boolean)
    .join('\n');

const Field: React.FC<FieldProps> = ({ label, value }) => {
  const styles = useStyles();

  return (
    <div className={styles.detailField}>
      <Typography className={styles.detailLabel} variant='body2'>
        {label}
      </Typography>
      <Typography className={styles.detailValue} variant='body1'>
        {value || '-'}
      </Typography>
    </div>
  );
};

const AssetSummarySection: React.FC<AssetSummarySectionProps> = ({
  caseAsset,
  dataFoundValues,
  editMode,
  onCompleted,
  onSaved,
  statusValues,
}) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [completionDialogOpen, setCompletionDialogOpen] = React.useState(false);
  const completedStatus = statusValues.find(value => value.code === CASE_ASSET_COMPLETED_STATUS_CODE);
  const statusLogValues = statusValues.filter(value => value.code !== CASE_ASSET_COMPLETED_STATUS_CODE);
  const incompleteRequestTypes = caseAsset.requestTypeResults.filter(result => !isRequestTypeFinal(result));

  const mutation = useMutation((payload: DsarCaseAssetUpdatePayload) => dsarApi.updateCaseAsset(caseAsset.id, payload), {
    onSuccess: response => {
      enqueueSnackbar(t(general_messages.data_saved), { variant: 'success' });
      onSaved(response.data);
      if (response.data.status.code === CASE_ASSET_COMPLETED_STATUS_CODE) {
        onCompleted();
      }
      queryClient.invalidateQueries('DSAR_CASE_ASSETS_GET_ALL');
    },
    onError: error => {
      enqueueSnackbar(getApiErrorMessage(error) || t(general_messages.api_error), { variant: 'error' });
    },
  });

  const statusLogMutation = useMutation(
    (payload: DsarCaseAssetStatusLogCreatePayload) => dsarApi.addCaseAssetStatusLog(caseAsset.id, payload),
    {
      onSuccess: response => {
        enqueueSnackbar(t(general_messages.data_saved), { variant: 'success' });
        onSaved(response.data);
        if (response.data.status.code === CASE_ASSET_COMPLETED_STATUS_CODE) {
          onCompleted();
        }
        queryClient.invalidateQueries('DSAR_CASE_ASSETS_GET_ALL');
      },
      onError: error => {
        enqueueSnackbar(getApiErrorMessage(error) || t(general_messages.api_error), { variant: 'error' });
      },
    },
  );

  const completionFormik = useFormik<CompletionValues>({
    initialValues: {
      dataFoundId: caseAsset.dataFound.id,
      resultSummary: caseAsset.resultSummary || '',
      assetOwnerComment: caseAsset.assetOwnerComment || '',
      thirdPartyDataRisk: Boolean(caseAsset.thirdPartyDataRisk),
      manualReviewRequired: Boolean(caseAsset.manualReviewRequired),
    },
    enableReinitialize: true,
    validate: values => {
      const errors: Partial<Record<keyof CompletionValues, string>> = {};
      const selectedDataFound = dataFoundValues.find(value => value.id === Number(values.dataFoundId));

      if (!completedStatus) {
        errors.resultSummary = t(dsar_messages.case_assets.validation.completed_status_missing);
      }
      if (!values.dataFoundId || selectedDataFound?.code === 'not_set') {
        errors.dataFoundId = t(dsar_messages.case_assets.validation.data_found_required_for_completion);
      }
      if (!values.resultSummary.trim()) {
        errors.resultSummary = t(dsar_messages.case_assets.validation.result_summary_required_for_completion);
      }
      if (!values.assetOwnerComment.trim()) {
        errors.assetOwnerComment = t(dsar_messages.case_assets.validation.asset_owner_comment_required_for_completed);
      }
      if (incompleteRequestTypes.length) {
        errors.resultSummary = t(dsar_messages.case_assets.validation.request_types_must_be_final_for_completion);
      }

      return errors;
    },
    onSubmit: values => {
      if (!completedStatus) return;

      const payload: DsarCaseAssetUpdatePayload = {
        status_id: completedStatus.id,
        data_found_id: Number(values.dataFoundId),
        result_summary: cleanOptional(values.resultSummary),
        asset_owner_comment: cleanOptional(values.assetOwnerComment),
        third_party_data_risk: values.thirdPartyDataRisk,
        manual_review_required: values.manualReviewRequired,
      };

      mutation.mutate(payload, {
        onSuccess: () => {
          setCompletionDialogOpen(false);
        },
      });
    },
  });
  const statusLogFormik = useFormik<AssetStatusLogValues>({
    initialValues: {
      statusId: caseAsset.status.code === CASE_ASSET_COMPLETED_STATUS_CODE ? statusLogValues[0]?.id || '' : caseAsset.status.id,
      comment: '',
    },
    enableReinitialize: true,
    validate: values => {
      const errors: Partial<Record<keyof AssetStatusLogValues, string>> = {};
      if (!values.statusId) {
        errors.statusId = t(dsar_messages.case_assets.validation.status_required_for_log);
      }
      if (!values.comment.trim()) {
        errors.comment = t(dsar_messages.case_assets.validation.comment_required_for_log);
      }
      return errors;
    },
    onSubmit: (values, helpers) => {
      statusLogMutation.mutate(
        {
          status_id: Number(values.statusId),
          comment: values.comment.trim(),
        },
        {
          onSuccess: () => {
            helpers.resetForm({ values: { statusId: Number(values.statusId), comment: '' } });
          },
        },
      );
    },
  });
  const handleCompletionManualReviewRequiredChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      completionFormik.setFieldValue('manualReviewRequired', event.target.checked);
    },
    [completionFormik],
  );
  const handleCompletionThirdPartyDataRiskChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      completionFormik.setFieldValue('thirdPartyDataRisk', event.target.checked);
    },
    [completionFormik],
  );
  const renderStatusLogs = () => (
    <div className={styles.statusLogList}>
      {(caseAsset.statusLogs || []).length ? (
        (caseAsset.statusLogs || []).map(log => (
          <div key={log.id} className={styles.statusLogRow}>
            <div className={styles.statusLogMeta}>
              <Chip label={log.status.name} size='small' />
              <Typography variant='body2'>{formatDateTime(log.loggedAt)}</Typography>
              {log.loggedByUserName && <Typography variant='body2'>{log.loggedByUserName}</Typography>}
            </div>
            <Typography className={styles.detailValue} variant='body2'>
              {log.comment || '-'}
            </Typography>
          </div>
        ))
      ) : (
        <Typography color='textSecondary' variant='body2'>
          {t(dsar_messages.case_assets.detail.status_log_empty)}
        </Typography>
      )}
    </div>
  );

  return (
    <>
      <div className={styles.sectionAccordion}>
        <CustomAccordion
          contentClassName={styles.accordionContent}
          heading={<Typography variant='h6'>{t(dsar_messages.case_assets.detail.summary_section)}</Typography>}
          iconsSet={undefined}
          id={`dsar-case-asset-summary-${caseAsset.id}`}
        >
          {editMode ? (
            <>
              <form onSubmit={statusLogFormik.handleSubmit}>
                <div className={styles.statusLogPanel}>
                  <Typography variant='subtitle1'>{t(dsar_messages.case_assets.detail.status_log)}</Typography>
                  {renderStatusLogs()}
                  <div className={styles.formGrid}>
                    <FormSelect
                      formik={statusLogFormik}
                      fullWidth
                      id='statusId'
                      label={t(dsar_messages.case_assets.table.status)}
                      options={toOptions(statusLogValues)}
                      required
                    />
                    <FormTextInput
                      className={styles.formFullWidth}
                      formik={statusLogFormik}
                      fullWidth
                      id='comment'
                      inputProps={{ className: styles.resizableTextarea }}
                      label={t(dsar_messages.case_assets.detail.status_log_comment)}
                      multiline
                      required
                      rows={2}
                    />
                  </div>
                  <div className={styles.sectionActions}>
                    <ColoredButton
                      className=''
                      customColor='secondary'
                      disabled={statusLogMutation.isLoading}
                      icon={false}
                      showLoader={statusLogMutation.isLoading}
                      textLike={false}
                      type='submit'
                      variant='contained'
                    >
                      {t(dsar_messages.case_assets.actions.add_status_log)}
                    </ColoredButton>
                  </div>
                </div>
              </form>
              {caseAsset.status.code !== CASE_ASSET_COMPLETED_STATUS_CODE && (
                <div className={styles.sectionActions}>
                  <ColoredButton
                    className=''
                    customColor='secondary'
                    disabled={mutation.isLoading}
                    icon={false}
                    onClick={() => setCompletionDialogOpen(true)}
                    showLoader={false}
                    textLike={false}
                    type='button'
                    variant='contained'
                  >
                    {t(dsar_messages.case_assets.actions.complete_asset_case)}
                  </ColoredButton>
                </div>
              )}
            </>
          ) : (
            <div className={styles.detailGrid}>
              <Field label={t(dsar_messages.case_assets.table.status)} value={caseAsset.status.name} />
              <div className={styles.formFullWidth}>
                <Typography className={styles.detailLabel} variant='body2'>
                  {t(dsar_messages.case_assets.detail.status_log)}
                </Typography>
                {renderStatusLogs()}
              </div>
              {caseAsset.status.code === CASE_ASSET_COMPLETED_STATUS_CODE && (
                <>
                  <Field label={t(dsar_messages.case_assets.table.data_found)} value={caseAsset.dataFound.name} />
                  <Field
                    label={t(dsar_messages.case_assets.detail.manual_review_required)}
                    value={t(caseAsset.manualReviewRequired ? general_messages.yes : general_messages.no)}
                  />
                  <Field
                    label={t(dsar_messages.case_assets.detail.third_party_data_risk)}
                    value={t(caseAsset.thirdPartyDataRisk ? general_messages.yes : general_messages.no)}
                  />
                  <Field label={t(dsar_messages.case_assets.detail.result_summary)} value={caseAsset.resultSummary} />
                  <Field label={t(dsar_messages.case_assets.detail.asset_owner_comment)} value={caseAsset.assetOwnerComment} />
                </>
              )}
            </div>
          )}
        </CustomAccordion>
      </div>
      <Dialog fullWidth maxWidth='md' onClose={() => setCompletionDialogOpen(false)} open={completionDialogOpen}>
        <DialogTitle>{t(dsar_messages.case_assets.completion.title)}</DialogTitle>
        <DialogContent dividers>
          <Typography className={styles.dialogIntro} color='textSecondary'>
            {t(dsar_messages.case_assets.completion.intro)}
          </Typography>
          {incompleteRequestTypes.length ? (
            <div className={styles.completionReadinessWarning}>
              <Typography variant='subtitle2'>{t(dsar_messages.case_assets.completion.request_types_not_ready)}</Typography>
              <ul className={styles.completionReadinessList}>
                {incompleteRequestTypes.map(result => (
                  <li key={result.id}>{`${result.requestType.name} - ${result.handlingStatus.name}`}</li>
                ))}
              </ul>
            </div>
          ) : (
            <Typography className={styles.completionReadinessOk} variant='body2'>
              {t(dsar_messages.case_assets.completion.request_types_ready)}
            </Typography>
          )}
          <form id='dsar-asset-completion-form' onSubmit={completionFormik.handleSubmit}>
            <div className={styles.formGrid}>
              <FormSelect
                formik={completionFormik}
                fullWidth
                id='dataFoundId'
                label={t(dsar_messages.case_assets.table.data_found)}
                options={toOptions(dataFoundValues)}
                required
              />
              <div className={styles.formFullWidth}>
                <div className={styles.booleanRow}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={completionFormik.values.manualReviewRequired}
                        color='primary'
                        onChange={handleCompletionManualReviewRequiredChange}
                      />
                    }
                    label={t(dsar_messages.case_assets.detail.manual_review_required)}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={completionFormik.values.thirdPartyDataRisk}
                        color='primary'
                        onChange={handleCompletionThirdPartyDataRiskChange}
                      />
                    }
                    label={t(dsar_messages.case_assets.detail.third_party_data_risk)}
                  />
                </div>
              </div>
              <FormTextInput
                className={styles.formFullWidth}
                formik={completionFormik}
                fullWidth
                id='resultSummary'
                inputProps={{ className: styles.resizableTextarea }}
                label={t(dsar_messages.case_assets.detail.result_summary)}
                multiline
                required
                rows={3}
              />
              <FormTextInput
                className={styles.formFullWidth}
                formik={completionFormik}
                fullWidth
                id='assetOwnerComment'
                inputProps={{ className: styles.resizableTextarea }}
                label={t(dsar_messages.case_assets.detail.asset_owner_comment)}
                multiline
                required
                rows={3}
              />
            </div>
          </form>
        </DialogContent>
        <DialogActions>
          <ColoredButton
            className=''
            customColor='secondary'
            disabled={mutation.isLoading}
            icon={false}
            onClick={() => setCompletionDialogOpen(false)}
            showLoader={false}
            textLike={false}
            variant='outlined'
          >
            {t(general_messages.cancel)}
          </ColoredButton>
          <ColoredButton
            className=''
            customColor='secondary'
            disabled={mutation.isLoading || Boolean(incompleteRequestTypes.length)}
            form='dsar-asset-completion-form'
            icon={false}
            showLoader={mutation.isLoading}
            textLike={false}
            type='submit'
            variant='contained'
          >
            {t(dsar_messages.case_assets.actions.complete_asset_case)}
          </ColoredButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

const RequestTypeSection: React.FC<RequestTypeSectionProps> = ({ editMode, handlingStatusValues, onSaved, result }) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const mutation = useMutation((payload: DsarCaseAssetRequestTypeUpdatePayload) => dsarApi.updateCaseAssetRequestType(result.id, payload), {
    onSuccess: response => {
      enqueueSnackbar(t(general_messages.data_saved), { variant: 'success' });
      onSaved(response.data);
      queryClient.invalidateQueries('DSAR_CASE_ASSETS_GET_ALL');
    },
    onError: error => {
      enqueueSnackbar(getApiErrorMessage(error) || t(general_messages.api_error), { variant: 'error' });
    },
  });

  const formik = useFormik<RequestTypeValues>({
    initialValues: {
      handlingStatusId: result.handlingStatus.id,
      resultSummary: result.resultSummary || '',
      blockingReason: result.blockingReason || '',
      earliestPossibleActionDate: toDateInputValue(result.earliestPossibleActionDate),
    },
    enableReinitialize: true,
    validate: values => {
      const errors: Partial<Record<keyof RequestTypeValues, string>> = {};
      const selectedStatus = handlingStatusValues.find(value => value.id === Number(values.handlingStatusId));
      const statusCode = selectedStatus?.code || '';
      const hasResultSummary = Boolean(values.resultSummary.trim());
      const hasBlockingReason = Boolean(values.blockingReason.trim());
      const hasEarliestDate = Boolean(values.earliestPossibleActionDate.trim());

      if (statusCode && statusCode !== 'not_started' && !hasResultSummary) {
        errors.resultSummary = t(dsar_messages.case_assets.validation.result_summary_required_after_start);
      }
      if (REQUEST_TYPE_BLOCKING_STATUS_CODES.includes(statusCode) && !hasBlockingReason) {
        errors.blockingReason = t(dsar_messages.case_assets.validation.blocking_reason_required_for_blocked);
      }
      if (!REQUEST_TYPE_BLOCKING_STATUS_CODES.includes(statusCode) && hasBlockingReason) {
        errors.blockingReason = t(dsar_messages.case_assets.validation.blocking_reason_only_for_blocked);
      }
      if (statusCode === 'deferred' && !hasEarliestDate) {
        errors.earliestPossibleActionDate = t(dsar_messages.case_assets.validation.earliest_date_required_for_deferred);
      }
      if (statusCode !== 'deferred' && hasEarliestDate) {
        errors.earliestPossibleActionDate = t(dsar_messages.case_assets.validation.earliest_date_only_for_deferred);
      }
      if (REQUEST_TYPE_FINAL_STATUS_CODES.includes(statusCode) && !hasResultSummary) {
        errors.resultSummary = t(dsar_messages.case_assets.validation.result_summary_required_for_final);
      }

      return errors;
    },
    onSubmit: values => {
      const payload: DsarCaseAssetRequestTypeUpdatePayload = {
        handling_status_id: Number(values.handlingStatusId),
        result_summary: cleanOptional(values.resultSummary),
        blocking_reason: cleanOptional(values.blockingReason),
        earliest_possible_action_date: cleanOptional(values.earliestPossibleActionDate),
      };
      mutation.mutate(payload);
    },
  });
  const selectedStatus = handlingStatusValues.find(value => value.id === Number(formik.values.handlingStatusId));
  const selectedStatusCode = selectedStatus?.code || '';
  const showBlockingReasonField = REQUEST_TYPE_BLOCKING_STATUS_CODES.includes(selectedStatusCode);
  const showEarliestDateField = selectedStatusCode === 'deferred';

  React.useEffect(() => {
    if (!showBlockingReasonField && formik.values.blockingReason) {
      formik.setFieldValue('blockingReason', '');
    }
    if (!showEarliestDateField && formik.values.earliestPossibleActionDate) {
      formik.setFieldValue('earliestPossibleActionDate', '');
    }
  }, [formik, formik.values.blockingReason, formik.values.earliestPossibleActionDate, showBlockingReasonField, showEarliestDateField]);

  return (
    <div className={styles.sectionAccordion}>
      <CustomAccordion
        contentClassName={styles.accordionContent}
        heading={
          <div className={styles.requestTypeHeader}>
            <Typography variant='h6'>{result.requestType.name}</Typography>
            <div className={styles.requestTypeMeta}>
              <Chip className={styles.requestTypeChip} label={result.handlingStatus.name} size='small' />
            </div>
          </div>
        }
        iconsSet={undefined}
        id={`dsar-case-asset-request-type-${result.id}`}
      >
        {editMode ? (
          <form onSubmit={formik.handleSubmit}>
            <div className={styles.formGrid}>
              <FormSelect
                formik={formik}
                fullWidth
                id='handlingStatusId'
                label={t(dsar_messages.case_assets.table.status)}
                options={toOptions(handlingStatusValues)}
                required
              />
              <div className={styles.formFullWidth}>
                <Typography className={styles.statusGuidance} variant='body2'>
                  {getRequestTypeStatusGuidanceMessage(selectedStatusCode, t)}
                </Typography>
              </div>
              {showEarliestDateField && (
                <FormTextInput
                  formik={formik}
                  fullWidth
                  id='earliestPossibleActionDate'
                  inputLabelProps={{ shrink: true }}
                  label={t(dsar_messages.case_assets.detail.earliest_possible_action_date)}
                  type='date'
                />
              )}
              <FormTextInput
                className={styles.formFullWidth}
                formik={formik}
                fullWidth
                id='resultSummary'
                inputProps={{ className: styles.resizableTextarea }}
                label={t(dsar_messages.case_assets.detail.result_summary)}
                multiline
                rows={2}
              />
              {showBlockingReasonField && (
                <FormTextInput
                  className={styles.formFullWidth}
                  formik={formik}
                  fullWidth
                  id='blockingReason'
                  inputProps={{ className: styles.resizableTextarea }}
                  label={t(dsar_messages.case_assets.detail.blocking_reason)}
                  multiline
                  rows={3}
                />
              )}
            </div>
            <div className={styles.sectionActions}>
              <ColoredButton
                className=''
                customColor='secondary'
                disabled={mutation.isLoading}
                icon={false}
                showLoader={mutation.isLoading}
                textLike={false}
                type='submit'
                variant='contained'
              >
                {t(dsar_messages.case_assets.actions.save_request_type)}
              </ColoredButton>
            </div>
          </form>
        ) : (
          <div className={styles.detailGrid}>
            <Field label={t(dsar_messages.case_assets.table.status)} value={result.handlingStatus.name} />
            <Field
              label={t(dsar_messages.case_assets.detail.earliest_possible_action_date)}
              value={formatDate(result.earliestPossibleActionDate)}
            />
            <Field label={t(dsar_messages.case_assets.detail.result_summary)} value={result.resultSummary} />
            <Field label={t(dsar_messages.case_assets.detail.blocking_reason)} value={result.blockingReason} />
            <Field label={t(dsar_messages.case_assets.table.completed_at)} value={formatDate(result.completedAt)} />
          </div>
        )}
      </CustomAccordion>
    </div>
  );
};

const DsarCaseAssetDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const styles = useStyles();
  const history = useHistory();
  const location = useLocation<LocationState>();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { id } = useParams<{ id: string }>();
  const [editMode, setEditMode] = React.useState(false);
  const { data: assetResponse, isLoading, error } = useQuery(['DSAR_CASE_ASSET_DETAIL', id], () => dsarApi.getSingleCaseAsset(Number(id)));
  const { data: caseAssetStatusValuesResponse } = useQuery(
    ['DSAR_DOMAIN_VALUES', CASE_ASSET_STATUS_GROUP],
    () => dsarApi.getDomainValues({ group: CASE_ASSET_STATUS_GROUP }),
    { retry: false, staleTime: 5 * 60 * 1000 },
  );
  const { data: caseAssetDataFoundValuesResponse } = useQuery(
    ['DSAR_DOMAIN_VALUES', CASE_ASSET_DATA_FOUND_GROUP],
    () => dsarApi.getDomainValues({ group: CASE_ASSET_DATA_FOUND_GROUP }),
    { retry: false, staleTime: 5 * 60 * 1000 },
  );
  const { data: requestTypeStatusValuesResponse } = useQuery(
    ['DSAR_DOMAIN_VALUES', CASE_ASSET_REQUEST_TYPE_STATUS_GROUP],
    () => dsarApi.getDomainValues({ group: CASE_ASSET_REQUEST_TYPE_STATUS_GROUP }),
    { retry: false, staleTime: 5 * 60 * 1000 },
  );

  const caseAsset = assetResponse?.data;
  const isCompleted = caseAsset?.status.code === 'completed' || Boolean(caseAsset?.completedAt);
  const caseAssetStatusValues = caseAssetStatusValuesResponse?.data || [];
  const caseAssetDataFoundValues = caseAssetDataFoundValuesResponse?.data || [];
  const requestTypeStatusValues = requestTypeStatusValuesResponse?.data || [];
  const reopenStatus =
    caseAssetStatusValues.find(value => value.code === 'in_progress') ||
    caseAssetStatusValues.find(value => value.code !== CASE_ASSET_COMPLETED_STATUS_CODE);

  const reopenMutation = useMutation(() => dsarApi.updateCaseAsset(Number(id), { status_id: reopenStatus?.id }), {
    onSuccess: response => {
      enqueueSnackbar(t(general_messages.data_saved), { variant: 'success' });
      queryClient.setQueryData(['DSAR_CASE_ASSET_DETAIL', id], { data: response.data });
      queryClient.invalidateQueries('DSAR_CASE_ASSETS_GET_ALL');
      setEditMode(true);
    },
    onError: mutationError => {
      enqueueSnackbar(getApiErrorMessage(mutationError) || t(general_messages.api_error), { variant: 'error' });
    },
  });

  const handleBack = React.useCallback(() => {
    history.push(PATHS.DSAR_CASE_ASSETS);
  }, [history]);

  const handleOpenEdit = React.useCallback(() => {
    setEditMode(true);
  }, []);

  const handleReopen = React.useCallback(() => {
    if (!reopenStatus) return;
    reopenMutation.mutate();
  }, [reopenMutation, reopenStatus]);

  const handleCancelEdit = React.useCallback(() => {
    setEditMode(false);
  }, []);

  const handleAssetSaved = React.useCallback(
    (updatedCaseAsset: DsarCaseAsset) => {
      queryClient.setQueryData(['DSAR_CASE_ASSET_DETAIL', id], { data: updatedCaseAsset });
    },
    [id, queryClient],
  );

  const handleRequestTypeSaved = React.useCallback(
    (updatedResult: DsarCaseAssetRequestTypeResult) => {
      queryClient.setQueryData(['DSAR_CASE_ASSET_DETAIL', id], (currentData: any) => {
        if (!currentData?.data) return currentData;
        return {
          data: {
            ...currentData.data,
            requestTypeResults: currentData.data.requestTypeResults.map((result: DsarCaseAssetRequestTypeResult) =>
              result.id === updatedResult.id ? updatedResult : result,
            ),
          },
        };
      });
    },
    [id, queryClient],
  );

  React.useEffect(() => {
    if (!location.state?.openEdit) return;
    setEditMode(true);
    history.replace({
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
      state: {},
    });
  }, [history, location]);

  if (isLoading) return <Loader inner />;

  if (error || !caseAsset) {
    return (
      <ErrorBoundary>
        <Container>
          <Typography color='error'>{t(general_messages.something_went_wrong)}</Typography>
        </Container>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <Container>
        <ManagePageHeader backButton onBack={handleBack}>
          {`${t(dsar_messages.case_assets.detail.title)} ${caseAsset.asset.name}`}
        </ManagePageHeader>
        <div className={styles.pageWrapper}>
          <Paper className={styles.heroCard} elevation={0}>
            <div className={styles.heroTop}>
              <div>
                <Typography variant='h6'>{caseAsset.asset.name}</Typography>
                <Typography color='textSecondary'>{`${caseAsset.dsarCase.caseReference} - ${caseAsset.asset.owner.fullName}`}</Typography>
              </div>
              <div className={styles.heroActions}>
                {!editMode && (
                  <ColoredButton
                    className={styles.heroActionButton}
                    customColor='secondary'
                    disabled={isCompleted ? !reopenStatus || reopenMutation.isLoading : false}
                    icon={false}
                    onClick={isCompleted ? handleReopen : handleOpenEdit}
                    showLoader={isCompleted && reopenMutation.isLoading}
                    textLike={false}
                    variant='contained'
                  >
                    {isCompleted ? t(dsar_messages.case_assets.actions.reopen) : t(dsar_messages.case_assets.actions.edit)}
                  </ColoredButton>
                )}
                {editMode && (
                  <ColoredButton
                    className={styles.heroActionButton}
                    customColor='secondary'
                    disabled={false}
                    icon={false}
                    onClick={handleCancelEdit}
                    showLoader={false}
                    textLike={false}
                    variant='outlined'
                  >
                    {t(dsar_messages.case_assets.actions.cancel_editing)}
                  </ColoredButton>
                )}
              </div>
            </div>
            <Typography className={clsx(styles.banner, isCompleted && styles.bannerWarning)} color='textPrimary'>
              {isCompleted ? t(dsar_messages.case_assets.detail.completed_banner) : t(dsar_messages.case_assets.detail.distributed_banner)}
            </Typography>
          </Paper>

          <div className={styles.caseContextAccordion}>
            <CustomAccordion
              contentClassName={styles.accordionContent}
              heading={<Typography variant='h6'>{t(dsar_messages.case_assets.detail.case_context)}</Typography>}
              iconsSet={undefined}
              id={`dsar-case-context-${caseAsset.id}`}
            >
              <Typography className={styles.accordionIntro} color='textSecondary'>
                {t(dsar_messages.case_assets.detail.case_context_intro)}
              </Typography>
              <Typography className={styles.sectionTitle} variant='h6'>
                {t(dsar_messages.case_assets.detail.data_subject_section)}
              </Typography>
              <div className={styles.personalDataWarning}>
                <Typography className={styles.personalDataWarningTitle} variant='body2'>
                  {t(dsar_messages.case_assets.detail.personal_data_warning_title)}
                </Typography>
                <Typography variant='body2'>{t(dsar_messages.case_assets.detail.personal_data_warning_body)}</Typography>
              </div>
              <div className={styles.detailGrid}>
                <Field label={t(dsar_messages.cases.create_dialog.requester_name)} value={caseAsset.dsarCase.requesterName} />
                <Field label={t(dsar_messages.cases.create_dialog.requester_email)} value={caseAsset.dsarCase.requesterEmail} />
                <Field label={t(dsar_messages.cases.create_dialog.requester_phone)} value={caseAsset.dsarCase.requesterPhone} />
                <Field
                  label={t(dsar_messages.cases.create_dialog.requester_customernumber)}
                  value={caseAsset.dsarCase.requesterCustomerNumber}
                />
                <Field label={t(dsar_messages.cases.create_dialog.requester_ssn)} value={caseAsset.dsarCase.requesterSsn} />
                <Field label={t(dsar_messages.cases.create_dialog.requester_address_line1)} value={formatAddress(caseAsset.dsarCase)} />
                <Field label={t(dsar_messages.case_assets.table.case_reference)} value={caseAsset.dsarCase.caseReference} />
                <Field label={t(dsar_messages.cases.table.status)} value={caseAsset.dsarCase.status.name} />
                <Field
                  label={t(dsar_messages.cases.table.identity_verification)}
                  value={caseAsset.dsarCase.identityVerificationStatus.name}
                />
                <Field label={t(dsar_messages.cases.table.received_at)} value={formatDate(caseAsset.dsarCase.receivedAt)} />
                <Field label={t(dsar_messages.case_assets.table.due_date)} value={formatDate(caseAsset.dsarCase.dueDate)} />
                <Field
                  label={t(dsar_messages.cases.create_dialog.admin_instruction_comment)}
                  value={caseAsset.dsarCase.adminInstructionComment}
                />
                <Field label={t(dsar_messages.cases.create_dialog.instruction_link)} value={caseAsset.dsarCase.instructionLink} />
                <Field
                  label={t(dsar_messages.cases.create_dialog.portability_instruction)}
                  value={caseAsset.dsarCase.portabilityInstruction}
                />
                <Field
                  label={t(dsar_messages.cases.create_dialog.portability_output_link)}
                  value={caseAsset.dsarCase.portabilityOutputLink}
                />
              </div>
            </CustomAccordion>
          </div>

          <div className={styles.sectionAccordion}>
            <CustomAccordion
              contentClassName={styles.accordionContent}
              heading={<Typography variant='h6'>{t(dsar_messages.case_assets.detail.asset_section)}</Typography>}
              iconsSet={undefined}
              id={`dsar-case-asset-details-${caseAsset.id}`}
            >
              <div className={styles.detailGrid}>
                <Field label={t(dsar_messages.case_assets.table.asset_name)} value={caseAsset.asset.name} />
                <Field label={t(dsar_messages.case_assets.table.owner)} value={caseAsset.asset.owner.fullName} />
                <Field label={t(general_messages.email)} value={caseAsset.asset.owner.email} />
                <Field label={t(dsar_messages.case_assets.table.completed_at)} value={formatDate(caseAsset.completedAt)} />
                <Field label={t(dsar_messages.case_assets.table.due_date)} value={formatDate(caseAsset.dueDate)} />
                <Field label={t(dsar_messages.case_assets.detail.case_details_section)} value={caseAsset.dsarCase.caseReference} />
              </div>
            </CustomAccordion>
          </div>

          <Paper className={styles.requestWorkPanel} elevation={0}>
            <Typography className={styles.sectionTitle} variant='h6'>
              {t(dsar_messages.case_assets.detail.request_work_section)}
            </Typography>
            <Typography className={styles.sectionIntroText} variant='body2'>
              {t(dsar_messages.case_assets.detail.request_work_intro)}
            </Typography>
            <div className={styles.requestWorkSections}>
              <AssetSummarySection
                caseAsset={caseAsset}
                dataFoundValues={caseAssetDataFoundValues}
                editMode={editMode}
                onCompleted={handleCancelEdit}
                onSaved={handleAssetSaved}
                statusValues={caseAssetStatusValues}
              />

              {caseAsset.requestTypeResults.map(result => (
                <RequestTypeSection
                  key={result.id}
                  editMode={editMode}
                  handlingStatusValues={requestTypeStatusValues}
                  onSaved={handleRequestTypeSaved}
                  result={result}
                />
              ))}
            </div>
          </Paper>
        </div>
      </Container>
    </ErrorBoundary>
  );
};

export default DsarCaseAssetDetailPage;
